import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  ActivateAutomaticSaleVehicleFinancingApiArg,
  ActivateAutomaticSaleVehicleFinancingApiResponse,
  DeleteSaleVehicleFinancingApiArg,
  DeleteSaleVehicleFinancingApiResponse,
  DeleteSaleVehicleLeasingApiArg,
  DeleteSaleVehicleLeasingApiResponse,
  GetSaleVehicleFinancingApiArg,
  GetSaleVehicleFinancingApiResponse,
  GetSaleVehicleLeasingApiArg,
  GetSaleVehicleLeasingApiResponse,
  SaveSaleVehicleFinancingApiArg,
  SaveSaleVehicleFinancingBodySchema,
  SaveSaleVehicleFinancingApiResponse,
  SaveSaleVehicleLeasingApiArg,
  SaveSaleVehicleLeasingBodySchema,
  SaveSaleVehicleLeasingApiResponse,
  SaleVehicleFinancingOverviewSchema,
  SaleVehicleLeasingOverviewSchema,
} from './types';

export const saleVehicleFinancingApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getSaleVehicleFinancing: build.query<
      GetSaleVehicleFinancingApiResponse,
      GetSaleVehicleFinancingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${encodeURIComponent(queryArg.saleVehicleId)}/financing`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SaleVehicleFinancing', id: queryArg.saleVehicleId},
      ],
      extraOptions: {
        responseSchema: SaleVehicleFinancingOverviewSchema,
      },
    }),
    saveSaleVehicleFinancing: build.mutation<
      SaveSaleVehicleFinancingApiResponse,
      SaveSaleVehicleFinancingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${encodeURIComponent(queryArg.saleVehicleId)}/financing`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: SaveSaleVehicleFinancingApiArg) => [
        {type: 'SaleVehicleFinancing', id: queryArg.saleVehicleId},
      ]),
      extraOptions: {
        requestSchema: SaveSaleVehicleFinancingBodySchema,
      },
    }),
    deleteSaleVehicleFinancing: build.mutation<
      DeleteSaleVehicleFinancingApiResponse,
      DeleteSaleVehicleFinancingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${encodeURIComponent(queryArg.saleVehicleId)}/financing/${queryArg.type}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: DeleteSaleVehicleFinancingApiArg) => [
          {type: 'SaleVehicleFinancing', id: queryArg.saleVehicleId},
        ]
      ),
    }),
    activateAutomaticSaleVehicleFinancing: build.mutation<
      ActivateAutomaticSaleVehicleFinancingApiResponse,
      ActivateAutomaticSaleVehicleFinancingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${encodeURIComponent(queryArg.saleVehicleId)}/financing/${queryArg.type}/automatic`,
        method: 'PUT',
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: ActivateAutomaticSaleVehicleFinancingApiArg) => [
          {type: 'SaleVehicleFinancing', id: queryArg.saleVehicleId},
        ]
      ),
    }),
    getSaleVehicleLeasing: build.query<
      GetSaleVehicleLeasingApiResponse,
      GetSaleVehicleLeasingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${encodeURIComponent(queryArg.saleVehicleId)}/leasing`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SaleVehicleLeasing', id: queryArg.saleVehicleId},
      ],
      extraOptions: {
        responseSchema: SaleVehicleLeasingOverviewSchema,
      },
    }),
    saveSaleVehicleLeasing: build.mutation<
      SaveSaleVehicleLeasingApiResponse,
      SaveSaleVehicleLeasingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${encodeURIComponent(queryArg.saleVehicleId)}/leasing`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: SaveSaleVehicleLeasingApiArg) => [
        {type: 'SaleVehicleLeasing', id: queryArg.saleVehicleId},
      ]),
      extraOptions: {
        requestSchema: SaveSaleVehicleLeasingBodySchema,
      },
    }),
    deleteSaleVehicleLeasing: build.mutation<
      DeleteSaleVehicleLeasingApiResponse,
      DeleteSaleVehicleLeasingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${encodeURIComponent(queryArg.saleVehicleId)}/leasing`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: DeleteSaleVehicleLeasingApiArg) => [
        {type: 'SaleVehicleLeasing', id: queryArg.saleVehicleId},
      ]),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSaleVehicleFinancingQuery,
  useSaveSaleVehicleFinancingMutation,
  useDeleteSaleVehicleFinancingMutation,
  useActivateAutomaticSaleVehicleFinancingMutation,
  useGetSaleVehicleLeasingQuery,
  useSaveSaleVehicleLeasingMutation,
  useDeleteSaleVehicleLeasingMutation,
} = saleVehicleFinancingApi;
